import {
  STRATEGY_CANVAS,
  STRATEGY_CANVAS_BRIEF,
  STRATEGY_CANVAS_REVIEW,
  STRATEGY_CANVAS_LIVE_REVIEW_WAITING,
  STRATEGY_CANVAS_VIEW,
  EXECUTION_BLUEPRINT,
  EXECUTION_BLUEPRINT_LIVE_REVIEW,
  EXECUTION_BLUEPRINT_REVIEW,
  EXECUTION_BLUEPRINT_WIG_CONNECTIONS,
  EXECUTION_BLUEPRINT_WIG_CONNECTIONS_LOADING,
  EXECUTION_BLUEPRINT_YEAR_ONE_PLAN,
  EXECUTION_BLUEPRINT_LOCKED_VIEW,
  ANNUAL_OKR,
  ANNUAL_OKR_LIVE_REVIEW,
  ANNUAL_OKR_REVIEW,
  ANNUAL_OKR_VIEW_OKRS,
  QUARTERLY_OKR,
  QUARTERLY_OKR_BRIEF,
  QUARTERLY_OKR_REVIEW,
  QUARTERLY_OKR_LIVE_REVIEW,
  QUARTERLY_OKR_SUMMARY,
  QUARTERLY_OKR_SCORECARD,
} from "./RouteUrls";

export const breadCrumbs = {
  strategyCanvas: {
    title: "Dashboard",
    url: STRATEGY_CANVAS,
  },
  strategyCanvasBrief: {
    title: "",
    url: STRATEGY_CANVAS_BRIEF,
  },
  strategyCanvasReview: {
    title: "Canvas Review",
    url: STRATEGY_CANVAS_REVIEW,
  },
  strategyCanvasLiveReviewWaiting: {
    title: "Live Review",
    url: STRATEGY_CANVAS_LIVE_REVIEW_WAITING,
  },
  strategyCanvasView: {
    title: "View",
    url: STRATEGY_CANVAS_VIEW,
  },
  // ── Execution Blueprint ───────────────────────────────────────────────────
  // ⚠️ The ROOT crumb's title is not just a crumb: both `ProjectHeader` and the
  // CEO app's panel derive `document.title` from `breadCrumbsArray[key][0].title`.
  // While this said "Dashboard", every Execution Blueprint screen reported
  // itself to the browser as "Art of Scale | Dashboard" — wrong in history, in
  // tab titles and in a bookmark. It names the feature now, matching the sidebar
  // item, the screen headers and the CEO app's own root crumb.
  executionBlueprint: {
    title: "Execution Blueprint",
    url: EXECUTION_BLUEPRINT,
  },
  // Overwritten at runtime with the client's company name — the coach works
  // across many clients, so the company IS the useful second crumb. The default
  // is a readable fallback rather than the empty string it used to be, which
  // rendered as a blank crumb and a stray separator before the dispatch landed.
  executionBlueprintReview: {
    title: "Blueprint Review",
    url: EXECUTION_BLUEPRINT_REVIEW,
  },
  executionBlueprintLiveReview: {
    title: "Live Review",
    url: EXECUTION_BLUEPRINT_LIVE_REVIEW,
  },
  // Stage 2 of the live review. Both the "building" wait and the connector map
  // share one label: they are one destination to the coach, and the wait is a
  // few seconds of it. A separate "Building…" crumb would flash past and then
  // be replaced, which is worse than a stable label.
  executionBlueprintWigConnections: {
    title: "WIG Connections",
    url: EXECUTION_BLUEPRINT_WIG_CONNECTIONS,
  },
  executionBlueprintYearOnePlan: {
    title: "Prioritised WIGs for the next 12 months",
    url: EXECUTION_BLUEPRINT_YEAR_ONE_PLAN,
  },

  // ── Annual OKRs ───────────────────────────────────────────────────────────
  // Names the feature, matching the sidebar item and the screen header, exactly
  // as `executionBlueprint` above does.
  annualOkr: {
    title: "Annual OKRs",
    url: ANNUAL_OKR,
  },
  annualOkrLiveReview: {
    title: "Live Review",
    url: ANNUAL_OKR_LIVE_REVIEW,
  },
  // Overwritten at runtime with the client's company name, like the blueprint's
  // review crumb — the coach works across many clients, so the company is the
  // useful second crumb. This is the readable pre-dispatch fallback.
  annualOkrReview: {
    title: "Annual OKR review",
    url: ANNUAL_OKR_REVIEW,
  },
  annualOkrViewOkrs: {
    title: "Annual OKR Scorecard",
    url: ANNUAL_OKR_VIEW_OKRS,
  },

  // ── Quarterly OKRs ────────────────────────────────────────────────────────
  // The child crumbs are overwritten at runtime with the client's name, like
  // the Annual OKR review's.
  quarterlyOkr: { title: "Quarterly OKRs", url: QUARTERLY_OKR },
  quarterlyOkrBrief: {
    title: "Brief",
    url: QUARTERLY_OKR_BRIEF,
  },
  quarterlyOkrReview: {
    title: "Quarterly OKR review",
    url: QUARTERLY_OKR_REVIEW,
  },
  quarterlyOkrLiveReview: {
    title: "Live Review",
    url: QUARTERLY_OKR_LIVE_REVIEW,
  },
  quarterlyOkrSummary: {
    title: "Quarter summary",
    url: QUARTERLY_OKR_SUMMARY,
  },
  quarterlyOkrScorecard: {
    title: "Scorecard",
    url: QUARTERLY_OKR_SCORECARD,
  },
};

export const breadCrumbsArray = {
  [STRATEGY_CANVAS]: [breadCrumbs.strategyCanvas],
  [STRATEGY_CANVAS_BRIEF]: [
    breadCrumbs.strategyCanvas,
    breadCrumbs.strategyCanvasBrief,
  ],
  [STRATEGY_CANVAS_REVIEW]: [
    breadCrumbs.strategyCanvas,
    breadCrumbs.strategyCanvasBrief,
    breadCrumbs.strategyCanvasReview,
  ],
  [STRATEGY_CANVAS_LIVE_REVIEW_WAITING]: [
    breadCrumbs.strategyCanvas,
    breadCrumbs.strategyCanvasBrief,
    breadCrumbs.strategyCanvasLiveReviewWaiting,
  ],
  [STRATEGY_CANVAS_VIEW]: [
    breadCrumbs.strategyCanvas,
    { title: "", url: STRATEGY_CANVAS_VIEW },
    breadCrumbs.strategyCanvasView,
  ],
  [EXECUTION_BLUEPRINT]: [breadCrumbs.executionBlueprint],
  [EXECUTION_BLUEPRINT_REVIEW]: [
    breadCrumbs.executionBlueprint,
    breadCrumbs.executionBlueprintReview,
  ],
  // ⚠️ TWO crumbs, and no "Blueprint Review" between them. The screen replaces
  // this at runtime with "‹Company› Live Review" as a SINGLE label (see its
  // dispatch); this is
  // the pre-dispatch fallback and the source of `document.title`, which
  // `ProjectHeader` reads from `[0].title` — so the root crumb stays HERE even
  // though the rendered trail drops it.
  //
  // The review crumb was removed on instruction: showing a path back to the
  // async review misleads, because the coach cannot return to it once the live
  // review has started.
  [EXECUTION_BLUEPRINT_LIVE_REVIEW]: [
    breadCrumbs.executionBlueprint,
    breadCrumbs.executionBlueprintLiveReview,
  ],

  // ── Stages 2 and 3 ───────────────────────────────────────────────────────
  // ⚠️ TWO crumbs each, and no "Blueprint Review" or "Live Review" between
  // them. Each screen replaces this at runtime with "‹Company› ⟨stage⟩" as a
  // SINGLE label (see their dispatches) — the company and the stage name one
  // thing, and splitting them read as a hierarchy with levels that don't exist.
  // Removed on instruction.
  //
  // What survives here is the pre-dispatch fallback and, from `[0].title`, the
  // source of `document.title` — which is why the root crumb stays in this file
  // even though the rendered trail drops it.
  [EXECUTION_BLUEPRINT_WIG_CONNECTIONS_LOADING]: [
    breadCrumbs.executionBlueprint,
    breadCrumbs.executionBlueprintWigConnections,
  ],
  [EXECUTION_BLUEPRINT_WIG_CONNECTIONS]: [
    breadCrumbs.executionBlueprint,
    breadCrumbs.executionBlueprintWigConnections,
  ],
  [EXECUTION_BLUEPRINT_YEAR_ONE_PLAN]: [
    breadCrumbs.executionBlueprint,
    breadCrumbs.executionBlueprintYearOnePlan,
  ],

  // ── After the terminal lock ──────────────────────────────────────────────
  // ⚠️ TWO crumbs — "Execution Blueprint › ‹tab›" — on instruction
  // (2026-09-02). The "Locked Blueprint" crumb that used to sit between them
  // pointed at the hub route, and both the hub and its route are gone; the
  // `breadCrumbs.executionBlueprintLocked` entry went with them, which also
  // brings this trail back in line with D41's two-crumb rule.
  //
  // The screen overwrites this at runtime with the active tab's label;
  // "Blueprint" is the pre-dispatch fallback.
  [EXECUTION_BLUEPRINT_LOCKED_VIEW]: [
    breadCrumbs.executionBlueprint,
    { title: "Blueprint", url: "" },
  ],

  // ── Annual OKRs ───────────────────────────────────────────────────────────
  // ⚠️ WITHOUT THESE THE HEADER RENDERS EMPTY. `ProjectHeader` walks
  // `Object.keys(breadCrumbsArray).reverse()`, returns the first `matchPath`
  // hit, and returns `[]` when nothing matches — so an unregistered route gets
  // a blank `#project-header` bar AND no `document.title`, which it also reads
  // from `[0].title`. Every Annual OKR screen was in that state. The CEO app
  // had the identical bug on its own Annual OKR routes (fixed 2026-09-21).
  //
  // ⚠️ ORDERING IS LOAD-BEARING: these must stay BELOW `[ANNUAL_OKR]`. Because
  // the keys are reversed, later entries are tested first — and `ANNUAL_OKR` is
  // not `exact`, so if it were tested first it would prefix-match every child
  // route and swallow their trails. The Execution Blueprint block above relies
  // on the same ordering.
  //
  // ⚠️ And within this block, the bare `[ANNUAL_OKR]` stays FIRST for the same
  // reason. Its two children are now siblings (`/review` and `/live-review`),
  // so their order relative to each other doesn't matter — but it did when one
  // of them was the bare `/:annualOkrId`, which prefix-matched the other.
  // Verified: with this ordering each of the three paths resolves to its own
  // key.
  [ANNUAL_OKR]: [breadCrumbs.annualOkr],
  [ANNUAL_OKR_REVIEW]: [breadCrumbs.annualOkr, breadCrumbs.annualOkrReview],
  [ANNUAL_OKR_VIEW_OKRS]: [
    breadCrumbs.annualOkr,
    breadCrumbs.annualOkrViewOkrs,
  ],
  [ANNUAL_OKR_LIVE_REVIEW]: [
    breadCrumbs.annualOkr,
    breadCrumbs.annualOkrLiveReview,
  ],
  // Same ordering rule: the bare dashboard first, so it is tested last.
  [QUARTERLY_OKR]: [breadCrumbs.quarterlyOkr],
  [QUARTERLY_OKR_BRIEF]: [
    breadCrumbs.quarterlyOkr,
    breadCrumbs.quarterlyOkrBrief,
  ],
  [QUARTERLY_OKR_REVIEW]: [
    breadCrumbs.quarterlyOkr,
    breadCrumbs.quarterlyOkrReview,
  ],
  [QUARTERLY_OKR_LIVE_REVIEW]: [
    breadCrumbs.quarterlyOkr,
    breadCrumbs.quarterlyOkrLiveReview,
  ],
  [QUARTERLY_OKR_SUMMARY]: [
    breadCrumbs.quarterlyOkr,
    breadCrumbs.quarterlyOkrSummary,
  ],
  [QUARTERLY_OKR_SCORECARD]: [
    breadCrumbs.quarterlyOkr,
    breadCrumbs.quarterlyOkrScorecard,
  ],
};
